import {
  getHeaderImage,
  LAURENT_LOGO,
  getContrastingBrandliveLogo,
  FOOTER_CARD_STYLE,
  getSessionDateLabel,
  getSessionTimeLabel,
  resolveThemedColor,
  resolveThemedFont,
  resolveThemedRadius,
  formatDateParts,
} from './utils/utils';
import { ECustomDesignTypes, EPaletteModes } from '@/types/theme-packs.types';
import type { TemplateProps } from './campaign-template.types';
import ContentEditable from './content-editable';
import styles from './template-styles.module.css';
import HeaderWidget from './widgets/header-widget';
import ImageWidget from './widgets/image-widget';
import ParagraphWidget from './widgets/paragraph-widget';
import FooterWidget from './widgets/footer-widget';
import LogoWidget from './widgets/logo-widget';
import SocialWidget from './widgets/social-widget';
import { ICONS } from '@ui/icons/types';
import { templateStaticDefaults } from './template-defaults';
import { BLOCK_ID } from './widgets/block-ids';
import { DateTimeLayout } from './widgets/date-time-layout';
import { BlockRenderer } from './widgets/block-renderer';
import BlockShell from '../editor/components/block-shell';
import CalendarLinksWidget from './widgets/calendar-links-widget';

const DEFAULTS = templateStaticDefaults.laurent;

const LAURENT_BLOCK_PRESETS: Record<string, Record<string, unknown>> = {
  [BLOCK_ID.headerLogo]: { height: 32, align: 'left', padding: '20px 40px', background: 'var(--heading)' },
  [BLOCK_ID.accentBar]: { background: 'var(--accent)' },
  [BLOCK_ID.title]: {
    color: 'var(--heading)',
    fontFamily: 'var(--heading-font)',
    fontSize: '40px',
    fontWeight: 700,
    lineHeight: '48px',
    padding: '20px 56px',
  },
  [BLOCK_ID.mainImage]: {
    width: 488,
    height: 280,
    borderRadius: 'var(--image-radius)',
    padding: '0 56px 20px 56px',
  },
  [BLOCK_ID.description]: {
    color: '#111111',
    fontFamily: 'var(--body-font)',
    fontSize: '24px',
    lineHeight: '29px',
    padding: '0 56px 20px 56px',
  },
  [BLOCK_ID.cta]: {
    fullWidth: true,
    height: '60px',
    buttonPadding: '0',
    color: 'var(--button-text)',
    buttonColor: 'var(--button-color)',
    fontFamily: 'var(--body-font)',
    fontWeight: 700,
    padding: '20px 56px',
  },
  [BLOCK_ID.divider]: { color: '#DBDBDB', padding: '0 56px' },
  [BLOCK_ID.social]: { color: 'var(--heading)', padding: '20px 56px 0 56px' },
  // [BLOCK_ID.supportText]: {
  //   color: '#888888',
  //   fontFamily: 'var(--body-font)',
  //   fontSize: '12px',
  //   fontWeight: 400,
  //   lineHeight: '18px',
  //   opacity: 0.7,
  //   padding: '20px 56px',
  // },
  [BLOCK_ID.legalText]: {
    color: '#1C1C1E',
    fontFamily: 'var(--body-font)',
    fontSize: '13px',
    fontWeight: 600,
    opacity: 0.4,
    padding: '20px 56px 40px 56px',
  },
  // the date & time block keeps laurent's original pair of cards
  [BLOCK_ID.dateTimeCard]: {
    layout: DateTimeLayout.Cards,
    background: 'var(--container)',
    padding: '0 56px 20px 56px',
    cardBackground: 'var(--bg)',
    borderStyle: 'none',
    labelStyle: { color: 'var(--body)', opacity: 0.7 },
    valueStyle: { color: '#666666' },
  },
  [BLOCK_ID.mailingAddress]: { ...FOOTER_CARD_STYLE, fontFamily: 'var(--body-font)' },
};

export const Laurent = ({
  event,
  theme,
  mode,
  timezone,
  href,
  calendarLinks,
  customizations,
  title,
  setTitle,
  description,
  setDescription,
  cta,
  setCta,
  setMainImage,
  setHeaderImage,
  blocks,
  onUpdateBlockValue,
  onUpdateBlockImage,
  onDeleteBlock,
  onMoveBlock,
  selectedBlockId,
  onSelectBlock,
  isEditMode,
  hideBrandliveLogo,
  privacyPolicyLabel,
}: TemplateProps) => {
  const resolvedMode = mode ?? theme?.theme_mode ?? EPaletteModes.Light;
  const themeColors = theme?.colors?.[resolvedMode] ?? theme?.colors;
  const bg = resolveThemedColor(
    customizations?.backgroundColor,
    themeColors?.backgroundColor,
    DEFAULTS.backgroundColor
  );
  const container = resolveThemedColor(
    customizations?.containerColor,
    themeColors?.containerColor,
    DEFAULTS.containerColor
  );
  const accent = resolveThemedColor(customizations?.accentColor, themeColors?.accentColor, DEFAULTS.accentColor);
  const heading = resolveThemedColor(
    customizations?.headingTextColor,
    themeColors?.headingTextColor,
    DEFAULTS.headingTextColor
  );
  const body = resolveThemedColor(customizations?.bodyTextColor, themeColors?.bodyTextColor, DEFAULTS.bodyTextColor);
  const buttonText = resolveThemedColor(
    customizations?.buttonTextColor,
    themeColors?.buttonTextColor,
    DEFAULTS.buttonTextColor ?? accent
  );
  const buttonColor = DEFAULTS.buttonColor ?? accent;
  const buttonRadius = resolveThemedRadius(theme?.custom_styles?.[ECustomDesignTypes.button], '100px');
  const containerRadius = resolveThemedRadius(theme?.custom_styles?.[ECustomDesignTypes.container], '0px');
  const imageRadius = resolveThemedRadius(theme?.custom_styles?.[ECustomDesignTypes.img], '20px');
  const sessionTimestamp = event.sessions[0]?.timestamp;
  const sessionDateLabel = getSessionDateLabel(sessionTimestamp, timezone, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
  const sessionTimeLabel = getSessionTimeLabel(sessionTimestamp, timezone);
  const sessionDateParts = sessionTimestamp ? formatDateParts(sessionTimestamp, timezone) : null;
  const anchorHref = href || '#';
  const headerImg = customizations?.headingImage || getHeaderImage(event) || LAURENT_LOGO;
  const bodyImg = customizations?.mainImage || DEFAULTS.image;
  const headingFont = resolveThemedFont(
    customizations?.headingFont,
    theme?.font_pack?.heading_name,
    '"Nunito Sans", Arial, serif'
  );
  const bodyFont = resolveThemedFont(
    customizations?.bodyFont,
    theme?.font_pack?.body_name,
    '"Nunito Sans", Arial, sans-serif'
  );
  const eventTitle = title ?? (customizations?.title || DEFAULTS.title);
  const eventDescription = description || customizations?.description || DEFAULTS.description;
  const _cta = cta ?? (customizations?.cta || DEFAULTS.cta);

  const bodyColor = container;
  const footerTextColor = body;
  const footerBackground = bg;
  const brandliveLogo = hideBrandliveLogo ? undefined : getContrastingBrandliveLogo(bodyColor);

  if (blocks?.length) {
    const theme = {
      bg,
      container,
      accent,
      buttonColor,
      heading,
      buttonText,
      body,
      headingFont,
      bodyFont,
      imageRadius,
      buttonRadius,
      containerRadius,
      bodyBg: bodyColor,
      footerText: footerTextColor,
      footerBg: footerBackground,
    };
    const blockContext = {
      blockPresets: LAURENT_BLOCK_PRESETS,
      brandliveLogo,
      privacyPolicyLabel,
      href: anchorHref,
      theme,
      dateLabel: timezone ? sessionDateLabel : null,
      timeLabel: timezone ? sessionTimeLabel : null,
      dateParts: timezone ? sessionDateParts : null,
    };

    return (
      <table
        data-component="Laurent"
        width="100%"
        cellPadding={0}
        cellSpacing={0}
        border={0}
        role="presentation"
        className={styles.email}
        style={
          {
            '--bg': bg,
            '--container': container,
            '--accent': accent,
            '--button-color': buttonColor,
            '--heading': heading,
            '--body': body,
            '--button-text': buttonText,
            '--heading-font': headingFont,
            '--body-font': bodyFont,
            '--image-radius': imageRadius,
            '--button-radius': buttonRadius,
            background: 'var(--bg)',
          } as React.CSSProperties
        }
      >
        <tbody>
          <tr>
            <td align="center" style={{ padding: '0 32px' }}>
              <table
                width={600}
                cellPadding={0}
                cellSpacing={0}
                border={0}
                role="presentation"
                style={{ maxWidth: '600px' }}
              >
                <tbody>
                  <tr>
                    <td style={{ background: 'var(--container)' }}>
                      {blocks.map((block) => {
                        return (
                          <BlockShell
                            key={block.id}
                            block={block}
                            isEditMode={!!isEditMode}
                            isFirst={block.id === blocks[0]?.id}
                            isLast={block.id === blocks[blocks.length - 1]?.id}
                            selected={block.id === selectedBlockId}
                            onSelect={onSelectBlock}
                            onDelete={onDeleteBlock}
                            onMoveUp={(id) => onMoveBlock?.(id, 'up')}
                            onMoveDown={(id) => onMoveBlock?.(id, 'down')}
                          >
                            <BlockRenderer
                              block={block}
                              stylePreset={LAURENT_BLOCK_PRESETS[block.id]}
                              onValueChange={onUpdateBlockValue}
                              onImageChange={onUpdateBlockImage}
                              context={blockContext}
                            />
                          </BlockShell>
                        );
                      })}
                    </td>
                  </tr>
                </tbody>
              </table>

              <table
                width={600}
                cellPadding={0}
                cellSpacing={0}
                border={0}
                role="presentation"
                style={{ maxWidth: '600px' }}
              >
                <tbody>
                  {!!calendarLinks && (
                    <tr>
                      <td align="center" style={{ padding: '24px 0' }}>
                        <CalendarLinksWidget links={calendarLinks} color="var(--body)" padding="0" />
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </td>
          </tr>
        </tbody>
      </table>
    );
  }

  return (
    <table
      data-component="Laurent"
      width="100%"
      cellPadding={0}
      cellSpacing={0}
      border={0}
      role="presentation"
      className={styles.email}
      style={
        {
          '--bg': bg,
          '--container': container,
          '--accent': accent,
          '--button-color': buttonColor,
          '--heading': heading,
          '--body': body,
          '--button-text': buttonText,
          '--heading-font': headingFont,
          '--body-font': bodyFont,
          background: 'var(--bg)',
        } as React.CSSProperties
      }
    >
      <tbody>
        <tr>
          <td align="center" style={{ padding: '0 32px' }}>
            <table
              width={600}
              cellPadding={0}
              cellSpacing={0}
              border={0}
              role="presentation"
              style={{ maxWidth: '600px' }}
            >
              <tbody>
                <tr>
                  <td style={{ background: 'var(--heading)' }}>
                    <LogoWidget
                      src={headerImg}
                      setImage={setHeaderImage}
                      height={32}
                      align="left"
                      padding="20px 40px"
                    />
                  </td>
                </tr>

                <tr>
                  <td height={4} style={{ background: 'var(--accent)', fontSize: 0, lineHeight: '0' }}>
                    &nbsp;
                  </td>
                </tr>

                <tr>
                  <td style={{ background: 'var(--container)' }}>
                    <HeaderWidget
                      value={eventTitle}
                      setValue={setTitle}
                      color="var(--heading)"
                      fontFamily="var(--heading-font)"
                      fontSize="40px"
                      fontWeight={700}
                      lineHeight="48px"
                      padding="20px 56px"
                    />

                    <ImageWidget
                      src={bodyImg}
                      setImage={setMainImage}
                      width={488}
                      height={280}
                      borderRadius={imageRadius}
                      padding="0 56px 20px 56px"
                    />

                    <ParagraphWidget
                      value={eventDescription}
                      setValue={setDescription}
                      color="#111111"
                      fontFamily="var(--body-font)"
                      fontSize="24px"
                      lineHeight="29px"
                      padding="0 56px 20px 56px"
                    />

                    {sessionTimestamp && (
                      <table role="presentation" width="100%" cellPadding={0} cellSpacing={0} border={0}>
                        <tbody>
                          <tr>
                            <td style={{ padding: '0 56px 20px 56px' }}>
                              <table width="100%" cellPadding={0} cellSpacing={0} border={0} role="presentation">
                                <tbody>
                                  <tr>
                                    <td width="50%" style={{ padding: '0 12px 0 0' }}>
                                      <table
                                        width="100%"
                                        cellPadding={0}
                                        cellSpacing={0}
                                        border={0}
                                        role="presentation"
                                        style={{ background: 'var(--bg)', borderRadius: '16px' }}
                                      >
                                        <tbody>
                                          <tr>
                                            <td style={{ padding: '20px' }}>
                                              <div
                                                style={{
                                                  color: 'var(--body)',
                                                  opacity: 0.7,
                                                  fontSize: '12px',
                                                  fontFamily: 'var(--body-font)',
                                                  fontWeight: 600,
                                                  textTransform: 'uppercase',
                                                  marginBottom: '8px',
                                                }}
                                              >
                                                <span style={{ color: 'var(--accent)' }}>●</span> Date
                                              </div>
                                              <div
                                                style={{
                                                  color: '#666666',
                                                  fontSize: '16px',
                                                  fontFamily: 'var(--body-font)',
                                                  fontWeight: 700,
                                                }}
                                              >
                                                {sessionDateLabel}
                                              </div>
                                            </td>
                                          </tr>
                                        </tbody>
                                      </table>
                                    </td>
                                    <td width="50%" style={{ padding: '0 0 0 12px' }}>
                                      <table
                                        width="100%"
                                        cellPadding={0}
                                        cellSpacing={0}
                                        border={0}
                                        role="presentation"
                                        style={{ background: 'var(--bg)', borderRadius: '16px' }}
                                      >
                                        <tbody>
                                          <tr>
                                            <td style={{ padding: '20px' }}>
                                              <div
                                                style={{
                                                  color: 'var(--body)',
                                                  opacity: 0.7,
                                                  fontSize: '12px',
                                                  fontFamily: 'var(--body-font)',
                                                  fontWeight: 600,
                                                  textTransform: 'uppercase',
                                                  marginBottom: '8px',
                                                }}
                                              >
                                                <span style={{ color: 'var(--accent)' }}>●</span> Time
                                              </div>
                                              <div
                                                style={{
                                                  color: '#666666',
                                                  fontSize: '16px',
                                                  fontFamily: 'var(--body-font)',
                                                  fontWeight: 700,
                                                }}
                                              >
                                                {sessionTimeLabel}
                                              </div>
                                            </td>
                                          </tr>
                                        </tbody>
                                      </table>
                                    </td>
                                  </tr>
                                </tbody>
                              </table>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    )}

                    <table role="presentation" width="100%" cellPadding={0} cellSpacing={0} border={0}>
                      <tbody>
                        <tr>
                          <td align="center" style={{ padding: '20px 56px' }}>
                            <table width="100%" cellPadding={0} cellSpacing={0} border={0} role="presentation">
                              <tbody>
                                <tr>
                                  <td
                                    align="center"
                                    valign="middle"
                                    style={{
                                      background: 'var(--button-color)',
                                      borderRadius: buttonRadius,
                                      height: '60px',
                                    }}
                                  >
                                    <a
                                      href={anchorHref}
                                      onClick={(e) => e.preventDefault()}
                                      style={{
                                        textDecoration: 'none',
                                        display: 'inline-block',
                                        height: '100%',
                                        textAlign: 'center',
                                      }}
                                    >
                                      <ContentEditable
                                        value={_cta}
                                        setValue={setCta}
                                        style={{
                                          color: 'var(--button-text)',
                                          fontSize: '16px',
                                          fontFamily: 'var(--body-font)',
                                          fontWeight: 700,
                                          lineHeight: '60px',
                                          wordWrap: 'break-word',
                                          whiteSpace: 'nowrap',
                                        }}
                                      />
                                    </a>
                                  </td>
                                </tr>
                              </tbody>
                            </table>
                          </td>
                        </tr>
                      </tbody>
                    </table>

                    <table role="presentation" width="100%" cellPadding={0} cellSpacing={0} border={0}>
                      <tbody>
                        <tr>
                          <td style={{ padding: '0 56px' }}>
                            <div style={{ borderTop: '1px solid #DBDBDB', fontSize: 0, lineHeight: '0' }}>&nbsp;</div>
                          </td>
                        </tr>
                      </tbody>
                    </table>

                    <SocialWidget
                      links={[
                        { platform: ICONS.FACEBOOK, href: '#' },
                        { platform: ICONS.INSTAGRAM, href: '#' },
                        { platform: ICONS.TWITTER, href: '#' },
                      ]}
                      color="var(--heading)"
                      padding="20px 56px 0 56px"
                    />

                    {/* <table role="presentation" width="100%" cellPadding={0} cellSpacing={0} border={0}>
                      <tbody>
                        <tr>
                          <td style={{ padding: '20px 56px' }}>
                            <p
                              style={{
                                margin: 0,
                                color: '#888888',
                                opacity: 0.7,
                                fontSize: '12px',
                                fontFamily: 'var(--body-font)',
                                fontWeight: 400,
                                lineHeight: '18px',
                                wordWrap: 'break-word',
                              }}
                            >
                              {DEFAULTS.supportText}
                            </p>
                          </td>
                        </tr>
                      </tbody>
                    </table> */}

                    <table role="presentation" width="100%" cellPadding={0} cellSpacing={0} border={0}>
                      <tbody>
                        <tr>
                          <td style={{ padding: '20px 56px 40px 56px' }}>
                            <p
                              style={{
                                margin: 0,
                                color: '#1C1C1E',
                                opacity: 0.4,
                                fontSize: '13px',
                                fontFamily: 'var(--body-font)',
                                fontWeight: 600,
                                wordWrap: 'break-word',
                              }}
                            >
                              {DEFAULTS.complianceText}
                            </p>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                    <FooterWidget
                      value={DEFAULTS.footerText}
                      src=""
                      brandliveLogo={brandliveLogo}
                      privacyPolicyLabel={privacyPolicyLabel}
                      bodyColor={bodyColor}
                      borderRadius={containerRadius}
                      accentColor={accent}
                      color={footerTextColor}
                      background={footerBackground}
                      {...FOOTER_CARD_STYLE}
                      fontFamily="var(--body-font)"
                    />
                  </td>
                </tr>
              </tbody>
            </table>

            <table
              width={600}
              cellPadding={0}
              cellSpacing={0}
              border={0}
              role="presentation"
              style={{ maxWidth: '600px' }}
            >
              <tbody>
                {!!calendarLinks && (
                  <tr>
                    <td align="center" style={{ padding: '24px 0' }}>
                      <table cellPadding={0} cellSpacing={0} border={0} role="presentation">
                        <tbody>
                          <tr>
                            <td>
                              <a
                                style={{
                                  color: 'var(--body)',
                                  fontSize: '12px',
                                  fontFamily: 'var(--body-font)',
                                  fontWeight: 600,
                                  lineHeight: '20px',
                                  letterSpacing: '0.24px',
                                }}
                                href={calendarLinks.iCal}
                              >
                                iCal
                              </a>
                            </td>
                            <td
                              style={{
                                padding: '0 4px',
                                color: 'var(--body)',
                                fontSize: '11px',
                                fontFamily: "'TASA Orbiter', sans-serif",
                                fontWeight: 600,
                                lineHeight: '16px',
                                letterSpacing: '0.22px',
                              }}
                            >
                              •
                            </td>
                            <td>
                              <a
                                style={{
                                  color: 'var(--body)',
                                  fontSize: '12px',
                                  fontFamily: 'var(--body-font)',
                                  fontWeight: 600,
                                  lineHeight: '20px',
                                  letterSpacing: '0.24px',
                                }}
                                href={calendarLinks.outlook}
                              >
                                Outlook
                              </a>
                            </td>
                            <td
                              style={{
                                padding: '0 4px',
                                color: 'var(--body)',
                                fontSize: '11px',
                                fontFamily: "'TASA Orbiter', sans-serif",
                                fontWeight: 600,
                                lineHeight: '16px',
                                letterSpacing: '0.22px',
                              }}
                            >
                              •
                            </td>
                            <td>
                              <a
                                style={{
                                  color: 'var(--body)',
                                  fontSize: '12px',
                                  fontFamily: 'var(--body-font)',
                                  fontWeight: 600,
                                  lineHeight: '20px',
                                  letterSpacing: '0.24px',
                                }}
                                href={calendarLinks.google}
                              >
                                Google
                              </a>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </td>
        </tr>
      </tbody>
    </table>
  );
};
